import React, { useMemo } from 'react';
import './CamelotWheel.css';

// Camelot wheel key positions (angle in degrees)
const CAMELOT_POSITIONS = {
  // Inner ring (Minor - A)
  '1A': { angle: 0, ring: 'inner' },
  '2A': { angle: 30, ring: 'inner' },
  '3A': { angle: 60, ring: 'inner' },
  '4A': { angle: 90, ring: 'inner' },
  '5A': { angle: 120, ring: 'inner' },
  '6A': { angle: 150, ring: 'inner' },
  '7A': { angle: 180, ring: 'inner' },
  '8A': { angle: 210, ring: 'inner' },
  '9A': { angle: 240, ring: 'inner' },
  '10A': { angle: 270, ring: 'inner' },
  '11A': { angle: 300, ring: 'inner' },
  '12A': { angle: 330, ring: 'inner' },
  // Outer ring (Major - B)
  '1B': { angle: 0, ring: 'outer' },
  '2B': { angle: 30, ring: 'outer' },
  '3B': { angle: 60, ring: 'outer' },
  '4B': { angle: 90, ring: 'outer' },
  '5B': { angle: 120, ring: 'outer' },
  '6B': { angle: 150, ring: 'outer' },
  '7B': { angle: 180, ring: 'outer' },
  '8B': { angle: 210, ring: 'outer' },
  '9B': { angle: 240, ring: 'outer' },
  '10B': { angle: 270, ring: 'outer' },
  '11B': { angle: 300, ring: 'outer' },
  '12B': { angle: 330, ring: 'outer' },
};

// Key colors (gradient around the wheel)
const KEY_COLORS = {
  '1A': '#ff6b6b', '1B': '#ff8787',
  '2A': '#ffa94d', '2B': '#ffc078',
  '3A': '#ffd43b', '3B': '#ffe066',
  '4A': '#a9e34b', '4B': '#c0eb75',
  '5A': '#69db7c', '5B': '#8ce99a',
  '6A': '#38d9a9', '6B': '#63e6be',
  '7A': '#3bc9db', '7B': '#66d9e8',
  '8A': '#4dabf7', '8B': '#74c0fc',
  '9A': '#748ffc', '9B': '#91a7ff',
  '10A': '#9775fa', '10B': '#b197fc',
  '11A': '#da77f2', '11B': '#e599f7',
  '12A': '#f783ac', '12B': '#faa2c1',
};

const CamelotWheel = ({ tracks = [], size = 280, showJourney = true }) => {
  const center = size / 2;
  const innerRadius = size * 0.25;
  const outerRadius = size * 0.4;
  const labelRadius = size * 0.32;

  // Get keys used in the crate - use camelot field first, fallback to key if it's in Camelot format
  const usedKeys = useMemo(() => {
    const keys = {};
    tracks.forEach((track, idx) => {
      // Prefer camelot field, fallback to key_override or key if they're in Camelot format
      let camelotKey = track.camelot;
      if (!camelotKey || !CAMELOT_POSITIONS[camelotKey]) {
        camelotKey = track.key_override || track.key;
      }
      if (camelotKey && CAMELOT_POSITIONS[camelotKey]) {
        if (!keys[camelotKey]) {
          keys[camelotKey] = [];
        }
        keys[camelotKey].push({ track, position: idx });
      }
    });
    return keys;
  }, [tracks]);

  // Create the journey path (connecting tracks in order)
  const journeyPath = useMemo(() => {
    if (!showJourney || tracks.length < 2) return null;
    
    const points = [];
    tracks.forEach((track) => {
      // Prefer camelot field, fallback to key_override or key if they're in Camelot format
      let camelotKey = track.camelot;
      if (!camelotKey || !CAMELOT_POSITIONS[camelotKey]) {
        camelotKey = track.key_override || track.key;
      }
      if (camelotKey && CAMELOT_POSITIONS[camelotKey]) {
        const pos = CAMELOT_POSITIONS[camelotKey];
        const radius = pos.ring === 'inner' ? innerRadius : outerRadius;
        const angleRad = (pos.angle - 90) * (Math.PI / 180);
        const x = center + radius * Math.cos(angleRad);
        const y = center + radius * Math.sin(angleRad);
        points.push({ x, y, key: camelotKey });
      }
    });
    
    if (points.length < 2) return null;
    
    // Create SVG path
    let d = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      d += ` L ${points[i].x} ${points[i].y}`;
    }
    
    return { d, points };
  }, [tracks, showJourney, center, innerRadius, outerRadius]);

  // Calculate position for a key
  const getKeyPosition = (key, radius) => {
    const pos = CAMELOT_POSITIONS[key];
    if (!pos) return { x: center, y: center };
    const angleRad = (pos.angle - 90) * (Math.PI / 180);
    return {
      x: center + radius * Math.cos(angleRad),
      y: center + radius * Math.sin(angleRad)
    };
  };

  return (
    <div className="camelot-wheel-container" data-testid="camelot-wheel">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background circles */}
        <circle
          cx={center}
          cy={center}
          r={outerRadius + 15}
          fill="rgba(0, 0, 0, 0.3)"
          stroke="rgba(255, 255, 255, 0.1)"
          strokeWidth="1"
        />
        
        {/* Outer ring segments (Major - B) */}
        {Object.entries(CAMELOT_POSITIONS)
          .filter(([_, pos]) => pos.ring === 'outer')
          .map(([key, pos]) => {
            const startAngle = pos.angle - 15;
            const endAngle = pos.angle + 15;
            const startRad = (startAngle - 90) * (Math.PI / 180);
            const endRad = (endAngle - 90) * (Math.PI / 180);
            
            const x1 = center + (outerRadius - 20) * Math.cos(startRad);
            const y1 = center + (outerRadius - 20) * Math.sin(startRad);
            const x2 = center + (outerRadius + 10) * Math.cos(startRad);
            const y2 = center + (outerRadius + 10) * Math.sin(startRad);
            const x3 = center + (outerRadius + 10) * Math.cos(endRad);
            const y3 = center + (outerRadius + 10) * Math.sin(endRad);
            const x4 = center + (outerRadius - 20) * Math.cos(endRad);
            const y4 = center + (outerRadius - 20) * Math.sin(endRad);
            
            const isUsed = !!usedKeys[key];
            
            return (
              <path
                key={key}
                d={`M ${x1} ${y1} L ${x2} ${y2} A ${outerRadius + 10} ${outerRadius + 10} 0 0 1 ${x3} ${y3} L ${x4} ${y4} A ${outerRadius - 20} ${outerRadius - 20} 0 0 0 ${x1} ${y1}`}
                fill={isUsed ? KEY_COLORS[key] : 'rgba(255, 255, 255, 0.05)'}
                stroke="rgba(255, 255, 255, 0.2)"
                strokeWidth="0.5"
                opacity={isUsed ? 0.9 : 0.4}
                className="wheel-segment"
              />
            );
          })}
        
        {/* Inner ring segments (Minor - A) */}
        {Object.entries(CAMELOT_POSITIONS)
          .filter(([_, pos]) => pos.ring === 'inner')
          .map(([key, pos]) => {
            const startAngle = pos.angle - 15;
            const endAngle = pos.angle + 15;
            const startRad = (startAngle - 90) * (Math.PI / 180);
            const endRad = (endAngle - 90) * (Math.PI / 180);
            
            const x1 = center + (innerRadius - 15) * Math.cos(startRad);
            const y1 = center + (innerRadius - 15) * Math.sin(startRad);
            const x2 = center + (innerRadius + 15) * Math.cos(startRad);
            const y2 = center + (innerRadius + 15) * Math.sin(startRad);
            const x3 = center + (innerRadius + 15) * Math.cos(endRad);
            const y3 = center + (innerRadius + 15) * Math.sin(endRad);
            const x4 = center + (innerRadius - 15) * Math.cos(endRad);
            const y4 = center + (innerRadius - 15) * Math.sin(endRad);
            
            const isUsed = !!usedKeys[key];
            
            return (
              <path
                key={key}
                d={`M ${x1} ${y1} L ${x2} ${y2} A ${innerRadius + 15} ${innerRadius + 15} 0 0 1 ${x3} ${y3} L ${x4} ${y4} A ${innerRadius - 15} ${innerRadius - 15} 0 0 0 ${x1} ${y1}`}
                fill={isUsed ? KEY_COLORS[key] : 'rgba(255, 255, 255, 0.03)'}
                stroke="rgba(255, 255, 255, 0.15)"
                strokeWidth="0.5"
                opacity={isUsed ? 0.9 : 0.3}
                className="wheel-segment"
              />
            );
          })}
        
        {/* Journey path */}
        {journeyPath && (
          <>
            <path
              d={journeyPath.d}
              fill="none"
              stroke="rgba(0, 255, 255, 0.6)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="journey-path"
            />
            {/* Journey points */}
            {journeyPath.points.map((point, idx) => (
              <circle
                key={idx}
                cx={point.x}
                cy={point.y}
                r={idx === 0 ? 6 : idx === journeyPath.points.length - 1 ? 6 : 4}
                fill={idx === 0 ? '#4ade80' : idx === journeyPath.points.length - 1 ? '#f87171' : '#00ffff'}
                stroke="white"
                strokeWidth="1"
                className="journey-point"
              />
            ))}
          </>
        )}
        
        {/* Key labels */}
        {Object.entries(CAMELOT_POSITIONS).map(([key, pos]) => {
          const radius = pos.ring === 'inner' ? innerRadius : outerRadius;
          const { x, y } = getKeyPosition(key, radius);
          const isUsed = !!usedKeys[key];
          const count = usedKeys[key]?.length || 0;
          
          return (
            <text
              key={`label-${key}`}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={isUsed ? "10" : "8"}
              fontWeight={isUsed ? "bold" : "normal"}
              fill={isUsed ? "white" : "rgba(255, 255, 255, 0.4)"}
              className="key-label"
            >
              {key}
              {count > 1 && (
                <tspan fontSize="7" dy="10" dx="-12">×{count}</tspan>
              )}
            </text>
          );
        })}
        
        {/* Center info */}
        <text
          x={center}
          y={center - 8}
          textAnchor="middle"
          fontSize="10"
          fill="rgba(255, 255, 255, 0.5)"
        >
          {Object.keys(usedKeys).length}
        </text>
        <text
          x={center}
          y={center + 6}
          textAnchor="middle"
          fontSize="8"
          fill="rgba(255, 255, 255, 0.4)"
        >
          keys
        </text>
      </svg>
      
      {/* Legend */}
      {showJourney && tracks.length > 0 && (
        <div className="wheel-legend">
          <div className="legend-item start">
            <span className="dot"></span>
            Start
          </div>
          <div className="legend-item end">
            <span className="dot"></span>
            End
          </div>
        </div>
      )}
    </div>
  );
};

export default CamelotWheel;
